// Comprehensive Data Models for Invoice System

import {
  PartyType,
  GSTType,
  AccountType,
  ItemTaxPreference,
  TaxMode,
  ItemUnit,
  PaymentMode,
  PaymentInfoStatus,
  InvoiceType,
  InvoiceSource,
  InvoiceStatus,
  Platform,
  PaymentStatus,
  CreditNoteType,
  CreditNoteReason,
  CreditNoteAdjustmentType,
  RefundMode,
  CreditNoteStatus,
  SalesReturnReason,
  SalesReturnAction,
  SalesReturnStatus,
  PlanTypeEnum,
  InvoiceTabType,
  InvoiceTabMode,
  SubscriptionStatus,
  BillingCycle,
  PaymentProvider,
} from './enums';

// ============================================
// PARTY (Customer/Supplier) TYPES
// ============================================

export interface Address {
  street?: string
  line1?: string
  line2?: string
  area?: string
  city?: string
  state?: string
  pinCode?: string
  pincode?: string
  country?: string
}

export interface PartyContact {
  name: string
  designation?: string
  phone: string
  email: string
  isPrimary: boolean
}

export interface GSTDetails {
  gstin?: string
  gstType?: string
  stateCode?: string
  registrationDate?: string
}

export interface BankDetails {
  accountName: string
  accountNumber: string
  bankName: string
  branch: string
  ifscCode: string
  accountType: AccountType
}

export interface Party {
  id: string
  type?: string

  name?: string // The primary name for the party

  // Basic Info
  /** @deprecated use name instead */
  companyName?: string
  /** @deprecated use name instead */
  displayName?: string
  contactPersonName?: string
  contactPerson?: string
  /** @deprecated use name instead */
  customerName?: string // for compatibility
  /** @deprecated use name instead */
  partyName?: string // for compatibility
  /** @deprecated use name instead */
  fullName?: string // for compatibility
  businessName?: string // for compatibility

  // Contact Details
  phone?: string
  email?: string
  website?: string
  contacts?: PartyContact[]

  // Address
  billingAddress?: Address
  shippingAddress?: Address
  sameAsShipping?: boolean
  address?: string
  city?: string
  pinCode?: string
  pincode?: string

  // GST & Tax
  gstDetails?: GSTDetails
  gstin?: string // Direct GSTIN field (alias for gstDetails.gstin)
  state?: string // Direct state field (extracted from billingAddress)
  stateCode?: string
  panNumber?: string
  tanNumber?: string

  // Bank Details
  bankDetails?: BankDetails

  // Business Details
  creditLimit?: number
  creditDays?: number
  paymentTerms?: string

  // Vehicle Information
  vehicleNo?: string

  // Metadata
  openingBalance?: number
  currentBalance?: number
  createdAt?: string
  updatedAt?: string
  createdBy?: string
  notes?: string
  tags?: string[]
  isActive?: boolean
  companyId?: string
  _pendingSync?: boolean
  _savedAt?: string | null
  _syncedAt?: string | null

  // Live Outstanding Balance (2025 Standard - Vyapar/Marg/Zoho style)
  // Outstanding = Opening Balance + Total Sales - Total Receipts - Credit Notes
  // Positive = they owe us (receivable for customer, payable for supplier)
  // Negative = we owe them (advance/credit)
  outstanding?: number
  outstandingFormatted?: string // e.g., "₹2,450" or "-₹1,200"
  outstandingColor?: 'green' | 'red' | 'grey' // green=receivable, red=payable, grey=settled
}

// ============================================
// ITEM/PRODUCT TYPES
// ============================================

export interface ItemTax {
  cgst: number
  sgst: number
  igst: number
  gstRate?: number
  cess?: number
  cessRate?: number
}

export interface Item {
  id: string

  // Basic Info
  name?: string
  description?: string
  itemCode?: string
  hsnCode?: string
  sacCode?: string
  hsn?: string

  // Classification
  category?: string
  subcategory?: string
  brand?: string

  // Pricing
  sellingPrice?: number
  salePrice?: number
  purchasePrice?: number
  mrp?: number
  unit?: string
  boxSellingPrice?: number
  boxPurchasePrice?: number

  // Tax
  taxPreference?: string
  tax?: ItemTax
  taxRate?: number

  // GST Tax Mode (Inclusive/Exclusive) - Separate for Selling & Purchase
  taxMode?: string // For selling price (default: 'inclusive')
  purchaseTaxMode?: string // For purchase price (default: 'inclusive')
  baseSellingPrice?: number // Auto-calculated base if selling is inclusive
  basePurchasePrice?: number // Auto-calculated base if purchase is inclusive

  // Inventory
  stock?: number
  quantity?: number
  openingStock?: number
  minStock?: number
  minStockLevel?: number
  maxStock?: number
  maxStockLevel?: number
  reorderPoint?: number
  retailPrice?: number
  type?: string

  // Multi-Unit Conversion
  hasMultiUnit?: boolean
  baseUnit?: string // e.g., 'Pcs' - smallest unit for stock tracking
  purchaseUnit?: string // e.g., 'Box' - unit for purchasing
  piecesPerPurchaseUnit?: number // e.g., 12 - how many pieces in one box
  sellingPricePerPiece?: number // Price per single piece
  purchasePricePerBox?: number // Price per box
  defaultSaleUnitId?: string // Default unit for sales ('Pcs' or 'Box')
  defaultPurchaseUnitId?: string // Default unit for purchases ('Box')
  stockBase?: number // Stock always in base units (pieces)

  // Metadata
  barcode?: string
  sku?: string
  imageUrl?: string
  expiryDate?: string  // Optional expiry date for perishable items
  isActive?: boolean
  createdAt?: string
  updatedAt?: string
  _pendingSync?: boolean
  _savedAt?: string | null
  _syncedAt?: string | null
}

// ============================================
// INVOICE/BILL TYPES
// ============================================

export interface InvoiceItem {
  id: string
  itemId?: string
  itemName?: string
  description?: string
  hsnCode?: string
  hsn?: string

  // Quantity & Pricing
  quantity?: number
  unit?: string
  rate?: number
  purchasePrice?: number  // For profit calculation

  // Discounts
  discountPercent?: number
  discountAmount?: number

  // Tax
  taxableAmount?: number
  cgstPercent?: number
  cgstAmount?: number
  sgstPercent?: number
  sgstAmount?: number
  igstPercent?: number
  igstAmount?: number
  cessPercent?: number
  cessAmount?: number
  taxRate?: number
  gstRate?: number
  tax?: number
  amount?: number
  purchasePricePerPiece?: number

  // Total
  totalAmount?: number

  // Profit (for sales invoices)
  profitMargin?: number  // Selling price - Purchase price
  profitPercent?: number  // (Profit / Selling price) * 100
}

export interface TransportDetails {
  transporterName?: string
  transporterGSTIN?: string
  vehicleNumber?: string
  dispatchDocNo?: string
  dispatchedThrough?: string
  destination?: string
  billOfLading?: string
  deliveryNoteDate?: string
}

export interface PaymentInfo {
  mode?: string
  status?: string
  paidAmount?: number
  dueAmount?: number
  dueDate?: string
  transactionId?: string
  chequeNumber?: string
  chequeDate?: string
  bankReference?: string
}

export interface CreditNote {
  id: string
  companyId?: string
  creditNoteNumber: string
  creditNoteDate: string
  type: CreditNoteType // credit for sales returns, debit for purchase returns

  // Reference to original invoice
  originalInvoiceId: string
  originalInvoiceNumber: string

  // Party Details
  partyId: string
  partyName: string
  partyGSTIN?: string

  // Items being returned/adjusted
  items: InvoiceItem[]

  // Reason
  reason: CreditNoteReason
  reasonDescription?: string

  // Amounts
  subtotal: number
  totalTaxAmount: number
  grandTotal: number

  // Adjustment
  adjustmentType: CreditNoteAdjustmentType
  refundAmount?: number
  refundMode?: RefundMode
  refundReference?: string  // For bank refunds: bank account name/number

  // Status
  status: CreditNoteStatus

  // Metadata
  createdAt: string
  updatedAt: string
  createdBy: string
  notes?: string
}

export interface SalesReturn {
  id: string
  returnNumber: string
  returnDate: string

  // Reference to original sale invoice
  originalInvoiceId: string
  originalInvoiceNumber: string

  // Customer
  customerId: string
  customerName: string

  // Items being returned
  items: Array<{
    itemId: string
    itemName: string
    quantityReturned: number
    rate: number
    amount: number
  }>

  // Reason
  reason: SalesReturnReason
  reasonDescription?: string

  // Total
  totalAmount: number

  // Action taken
  action: SalesReturnAction
  refundAmount?: number

  // Status
  status: string

  // Auto-generated credit note
  creditNoteId?: string

  // Metadata
  createdAt: string
  updatedAt: string
  approvedBy?: string
}

export interface Invoice {
  id: string
  type: string
  source?: string // Track where invoice was created from

  // Invoice Details
  invoiceNumber: string
  invoiceDate: string
  dueDate?: string

  // Party Details
  partyId?: string
  partyName?: string
  partyGSTIN?: string
  partyStateCode?: string
  partyAddress?: any
  partyPhone?: string
  partyEmail?: string

  // Business Details (Self)
  businessName?: string
  businessGSTIN?: string
  businessAddress?: any
  businessPhone?: string
  businessEmail?: string
  businessStateCode?: string
  billingStateCode?: string

  // Reference
  referenceNumber?: string
  referenceDate?: string
  buyerOrderNumber?: string
  buyerOrderDate?: string
  deliveryNoteNumber?: string

  // Items
  items: InvoiceItem[]

  // Amounts
  subtotal?: number
  discountPercent?: number
  discountAmount?: number
  taxableAmount?: number

  // Tax Breakdown
  cgstAmount?: number
  sgstAmount?: number
  igstAmount?: number
  cessAmount?: number
  totalTaxAmount?: number
  taxAmount?: number

  // Additional Charges
  shippingCharges?: number
  otherCharges?: number
  roundOff?: number

  // Total
  grandTotal?: number

  // Profit (for sales invoices)
  totalProfit?: number  // Sum of all item profit margins
  totalProfitPercent?: number  // (Total Profit / Grand Total) * 100

  // Payment
  payment?: PaymentInfo

  // Transport
  transport?: TransportDetails

  // Terms & Conditions
  termsOfDelivery?: string
  paymentTerms?: string
  notes?: string

  // Metadata
  status?: InvoiceStatus | 'draft' | 'approved' | 'sent' | 'paid' | 'cancelled'
  createdAt?: string
  updatedAt?: string
  createdBy?: string
  createdByUserId?: string
  createdByUserRole?: string
  createdByUserName?: string

  // E-Invoice (if applicable)
  eInvoiceNumber?: string
  eInvoiceIRN?: string
  eInvoiceAckNo?: string
  eInvoiceAckDate?: string
  eInvoiceQRCode?: string

  // Marketplace/Channel Details (for GST Sales Register - Output Tax)
  // Used to calculate correct taxable value for marketplaces like Amazon, Flipkart, Meesho etc.
  platform?: Platform
  platformName?: string // Custom platform name if 'other'
  orderId?: string // Marketplace order ID (e.g., AMZ2025-12345)

  // Deductions for taxable value calculation (GST Compliance)
  shippingChargedToCustomer?: number // Shipping charged separately to customer
  tcsCollected?: number // TCS (1%) collected by marketplace - deducted from settlement
  tdsDeducted?: number // TDS deducted by marketplace (if applicable)
  marketplaceFees?: number // Commission + other marketplace fees
  marketplaceCommissionPercent?: number // Commission percentage for reference

  // Settlement Details
  settlementAmount?: number // Actual amount received from marketplace
  settlementDate?: string
  settlementReference?: string

  // Place of Supply (for IGST determination)
  placeOfSupply?: string // State code or state name
  isInterState?: boolean // true = IGST, false = CGST+SGST

  // Reverse Charge (RCM)
  isReverseCharge?: boolean
  rcmAmount?: number

  // Additional properties for compatibility with Sales.tsx
  customerName?: string // Alias for partyName
  supplierId?: string
  supplierName?: string
  supplierEmail?: string
  total?: number // Alias for grandTotal
  convertedFrom?: string // If converted from quotation
  paidAmount?: number // Amount already paid
  amountPaid?: number
  paymentStatus?: string // Payment status
  paymentMode?: string // Payment method used
  vehicleNo?: string // Vehicle number for transport
  partyVehicleNo?: string
  isReversed?: boolean // If invoice was reversed
  _pendingSync?: boolean
  _savedAt?: string | null
  _syncedAt?: string | null

  // Staff who handled the invoice
  staff_code?: string // Staff code from Staff Master (e.g., "ST001"), nullable
  staffCode?: string
  staff_name?: string
  staffName?: string

  // Billing Staff (Accountant) who created the bill
  billingStaffId?: string
  billingStaffName?: string
  billingStaffRole?: string
  billingStaffEmail?: string

  // Return tracking
  hasReturns?: boolean // If invoice has any returns
  returnedAmount?: number // Total returned amount
  returnedCount?: number
}

export interface LedgerEntry {
  id: string
  partyId: string
  partyName: string
  date: string
  companyId?: string
  type: 'invoice' | 'payment' | 'opening_balance' | 'adjustment'
  referenceType: 'sale' | 'purchase' | 'payment' | 'opening' | 'adjustment'
  referenceNumber: string
  description: string
  debit: number
  credit: number
  balance: number
  createdAt: string
}

// ============================================
// RECEIPT SCANNER TYPES
// ============================================

export interface ScannedInvoiceData {
  // Vendor/Supplier Info
  vendor: {
    name: string
    address?: string
    city?: string
    state?: string
    pinCode?: string
    gstin?: string
    stateCode?: string
    phone?: string
    email?: string
  }

  // Buyer Info
  buyer?: {
    name: string
    address?: string
    city?: string
    state?: string
    pinCode?: string
    gstin?: string
    stateCode?: string
  }

  // Invoice Details
  invoiceNumber: string
  invoiceDate: string
  deliveryNoteNumber?: string
  referenceNumber?: string
  buyerOrderNumber?: string
  dispatchDocNumber?: string
  deliveryNoteDate?: string
  vehicleNumber?: string
  destination?: string

  // Items
  items: Array<{
    description: string
    hsnCode?: string
    quantity: number
    unit: string
    rate: number
    amount: number
    taxableAmount?: number
    cgstRate?: number
    cgstAmount?: number
    sgstRate?: number
    sgstAmount?: number
    igstRate?: number
    igstAmount?: number
    totalAmount?: number
  }>

  // Tax & Totals
  taxableValue: number
  cgstRate: number
  cgstAmount: number
  sgstRate: number
  sgstAmount: number
  igstRate?: number
  igstAmount?: number
  totalTaxAmount: number
  roundOff: number
  grandTotal: number

  // Payment
  paymentMode?: string
  termsOfPayment?: string
}

// ============================================
// SETTINGS TYPES
// ============================================

export interface BusinessSettings {
  // Company Info
  companyName: string
  displayName: string
  logo?: string

  // Contact
  phone: string
  email: string
  website?: string

  // Address
  address: Address

  // GST & Tax
  gstin?: string
  stateCode: string
  panNumber?: string
  tanNumber?: string

  // Bank
  bankDetails?: BankDetails

  // Invoice Settings
  invoicePrefix: string
  invoiceStartNumber: number
  billPrefix: string
  billStartNumber: number

  // Tax Settings
  defaultTaxRate: number
  enableGST: boolean
  enableEInvoice: boolean

  // Other
  fiscalYearStart: string
  currency: string
  dateFormat: string
  timezone: string
}

// ============================================
// API RESPONSE TYPES
// ============================================

export interface APIResponse<T> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

// ============================================
// SUBSCRIPTION PLAN TYPES
// ============================================

export type PlanType = PlanTypeEnum;

export interface PlanFeatures {
  // Core Features (Available in Silver Plan)
  basicInvoicing: boolean
  partyManagement: boolean
  inventoryBasic: boolean
  advancedReports: boolean
  exportToPDF: boolean
  exportToExcel: boolean
  whatsappIntegration: boolean
  paymentReminders: boolean
  gstReports: boolean
  cashFlowReports: boolean
  profitLossReports: boolean

  // Premium Features (Gold only - Others Section)
  onlineStore: boolean
  deliveryChallan: boolean
  purchaseOrders: boolean
  ewayBiller: boolean
  staffAttendance: boolean
  salaryManagement: boolean
  performanceInvoice: boolean
  multipleLocations: boolean
}

export interface SubscriptionPlan {
  type: PlanType
  name: string
  features: PlanFeatures
  price?: number
  description?: string
  maxUsers?: number
}

export const PLAN_CONFIG: Record<PlanType, SubscriptionPlan> = {
  [PlanTypeEnum.SILVER]: {
    type: PlanTypeEnum.SILVER,
    name: 'Silver Plan',
    description: 'Full-featured CRM with core business tools',
    maxUsers: 5,
    features: {
      // All available in Silver
      basicInvoicing: true,
      partyManagement: true,
      inventoryBasic: true,
      advancedReports: true,
      exportToPDF: true,
      exportToExcel: true,
      whatsappIntegration: true,
      paymentReminders: true,
      gstReports: true,
      cashFlowReports: true,
      profitLossReports: true,

      // Locked in Silver (Others Section only)
      onlineStore: false,
      deliveryChallan: true,
      purchaseOrders: true,
      ewayBiller: false,
      staffAttendance: false,
      salaryManagement: false,
      performanceInvoice: true,
      multipleLocations: false,
    }
  },
  [PlanTypeEnum.GOLD]: {
    type: PlanTypeEnum.GOLD,
    name: 'Gold Plan',
    description: 'Complete enterprise solution with all premium features',
    maxUsers: 25,
    features: {
      // All features available
      basicInvoicing: true,
      partyManagement: true,
      inventoryBasic: true,
      advancedReports: true,
      exportToPDF: true,
      exportToExcel: true,
      whatsappIntegration: true,
      paymentReminders: true,
      gstReports: true,
      cashFlowReports: true,
      profitLossReports: true,
      onlineStore: true,
      deliveryChallan: true,
      purchaseOrders: true,
      ewayBiller: true,
      staffAttendance: true,
      salaryManagement: true,
      performanceInvoice: true,
      multipleLocations: true,
    }
  }
}

// Extended types to resolve missing properties - These are added to main Invoice interface above

export interface InvoiceTab {
  id: string;
  type: InvoiceTabType;
  mode: InvoiceTabMode;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  customerGST: string;
  customerState: string;
  invoiceItems: InvoiceItem[];
  paymentMode: string;
  invoiceDiscount: number;
  notes: string;
  customerSearch: string;
}

export interface CartItem {
  id: string;
  itemId: string;
  name: string;
  price: number;
  quantity: number;
  unit: string;
  tax: number;
  taxAmount: number;
  hsnCode?: string;
  discount?: number;
  gstRate?: number;
}

export interface CRMEngineer {
  id: string;
  name: string;
  email: string;
  phone: string;
  specialization: string;
  experience: number; // years
  status: 'active' | 'inactive';
  companyId: string;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================
// STAFF MASTER TYPES
// ============================================

export interface Staff {
  id: string;
  staff_code: string;      // e.g., "ST001"
  staff_name: string;      // e.g., "Ganesh"
  phone?: string;
  email?: string;
  designation?: string;
  department?: string;
  isActive: boolean;
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// SUBSCRIPTION TYPES
// ============================================

export interface CompanySubscription {
  id: string
  companyId: string
  planType: PlanType
  status: SubscriptionStatus

  // Trial Info
  trialStartDate: string
  trialEndDate: string
  trialDays: number // Total trial days (default 7)

  // Subscription Period
  subscriptionStartDate?: string
  subscriptionEndDate?: string
  nextBillingDate?: string
  billingCycle?: BillingCycle
  remainingDays?: number

  // Payment
  paymentProvider: PaymentProvider
  lastPaymentDate?: string
  lastPaymentAmount?: number
  razorpaySubscriptionId?: string
  razorpayCustomerId?: string

  // Pricing (in INR)
  monthlyPrice: number  // ₹499
  yearlyPrice: number   // ₹4,999

  // Auto-renewal
  autoRenew: boolean

  // Grace period (days after expiry where user can still pay)
  gracePeriodDays: number // default 3

  // Metadata
  createdAt: string
  updatedAt: string
  cancelledAt?: string
  cancelReason?: string
}

export interface BillingRecord {
  id: string
  companyId: string
  subscriptionId: string

  // Amount
  amount: number
  currency: string // INR

  // Dates
  billingDate: string
  dueDate: string
  paidDate?: string

  // Status
  status: 'pending' | 'paid' | 'failed' | 'refunded'

  // Payment details
  razorpayPaymentId?: string
  razorpayOrderId?: string
  paymentMethod?: string // upi, card, netbanking

  // Plan details
  planType?: PlanType
  billingCycle?: BillingCycle

  // Invoice
  invoiceNumber?: string
  invoiceUrl?: string
  taxRate?: number
  taxAmount?: number

  // Metadata
  createdAt: string
  updatedAt: string
  notes?: string
}

// Subscription pricing configuration
export const SUBSCRIPTION_PRICING = {
  monthly: 499,   // ₹499/month
  yearly: 4999,   // ₹4,999/year (save ₹989)
  trialDays: 7,
  gracePeriodDays: 3,
} as const

export const PLAN_PRICING: Record<PlanType, { monthly: number; yearly: number; maxUsers?: number }> = {
  [PlanTypeEnum.SILVER]: { monthly: 499, yearly: 4999, maxUsers: 5 },
  [PlanTypeEnum.GOLD]: { monthly: 999, yearly: 9999, maxUsers: 25 }
}

// Helper type for subscription state in UI
export interface SubscriptionState {
  isActive: boolean
  isTrialing: boolean
  isExpired: boolean
  isGracePeriod: boolean
  daysRemaining: number
  canEdit: boolean // false when expired (view-only mode)
  showExpiryWarning: boolean // true when < 5 days remaining
  subscription: CompanySubscription | null
}
